/**
 * ONE CALL, EXPLAINED — in two layers, because two audiences read it.
 *
 * THE PAGE is for the person who wants to know what happened on the call:
 *
 *   1. WHAT THIS CALL WAS   identity, both state enums, cost, provider call id
 *   2. WHAT WAS SAID        the recording as stacked waveforms with one shared
 *                           playhead, and the transcript as a thread — speaker
 *                           badges, inline tool rows, our own gaps marked in the
 *                           reading order. Playback highlights the row being
 *                           spoken (product-owner direction 2026-08-23, modelled
 *                           on voice2me's session view).
 *
 * THE TECH STATS DRAWER (button in the header) is for the person diagnosing it:
 * the turn timeline (who was slow), the per-track recordings with their
 * completeness claims, and the tool-call table with arguments and results. The
 * per-track honesty rules move with the players and still hold: a recording
 * that is not COMPLETE is never presented as if it were whole, and PARTIAL
 * ("we measured a gap of 1.4 s") and UNVERIFIED ("we could not measure, and
 * cannot tell you") are worded differently.
 *
 * THE OLD "WHAT WE CANNOT TELL YOU" SECTION IS GONE — removed by the product
 * owner 2026-08-23. The claims it made did not vanish with it: the ones tied to
 * a specific number are stated where that number is (the timeline's legend and
 * per-turn pills carry the playout-contamination and buffer-unchecked caveats;
 * the track players carry provenance and completeness), which is where a reader
 * meets them anyway.
 *
 * A 404 IS A SCREEN, NOT AN ERROR BOX. Session ids are pasted into chat windows
 * and truncated by mail clients, so "no such session" is a routine outcome and
 * gets a sentence rather than a red banner.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIcon, ScrollTextIcon } from 'lucide-react'
import {
  ApiError,
  sessions,
  type AudioUrl,
  type RecordingRow,
  type SessionDetail,
  type SessionEventRow,
  type ToolCallRow,
  type TurnRow,
} from '@/api'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Link } from '@/components/app-sidebar'
import { TurnTimeline } from '@/components/turn-timeline'
import { EventLogPanel } from '@/components/call/event-log'
import { SessionAudioPlayer, splitRecordingsByLeg } from '@/components/session/audio-player'
import { TranscriptThread } from '@/components/session/transcript-thread'
import { LiveConversationThread } from '@/components/session/live-conversation'
import { LiveMonitor } from '@/components/session/live-monitor'
import { Field, Mono } from '@/components/session/fields'
import {
  DASH,
  EvidencePill,
  RecordingPill,
  StatePill,
  TransportIcon,
  fmtBytes,
  fmtClock,
  fmtCost,
  fmtDuration,
  fmtInt,
  fmtMs,
  fmtPct,
  fmtSeconds,
  fmtWhen,
  isLive,
} from '@/components/state-pill'

const POLL_MS = 5_000

/**
 * `state_detail` used to be part prose and part control state — the jobs sweeper
 * recorded "have I settled this row?" as a `[sweeper:settled]` text PREFIX on it,
 * and this file split the two back apart with a regex. The sweeper's bookkeeping
 * now lives in columns of its own (`sweepState`, `sweptAt`), `state_detail` is
 * purely the end reason as whoever observed it wrote it, and nothing here parses
 * anything: the reason renders as a sentence, the flag renders as a pill.
 */
function sweptTitle(sweptAt: string, pending: boolean): string {
  return (
    `The jobs sweeper resolved this row at ${fmtWhen(sweptAt)}: the provider no longer ` +
    'knew the call (sideband re-attach 404). If nothing recorded the end before then, ' +
    'the duration is an upper bound — the sweeper runs every five minutes.' +
    (pending ? ' Artefacts were still missing, so the row stays in the sweep.' : '')
  )
}

/**
 * Which tracks this transport OWES, minus the ones that have a row.
 *
 * A `telephony-dual` session owes one carrier-side file; a browser session owes
 * both sides, because the browser is the only custodian of either (§4b). Rendered
 * beside the players in the tech stats drawer, where the absence matters most.
 */
function missingExpectedTracks(
  transport: 'webrtc' | 'sip',
  recordings: RecordingRow[],
  /** The session's terminal state. `ENDED_TRANSFERRED` and `ENDED_TAKEN_OVER`
   *  each add an expectation, and each adds a whole conversation's worth. */
  state: string,
): string[] {
  const expected = transport === 'sip' ? ['telephony-dual'] : ['user', 'assistant']
  /**
   * A TRANSFERRED SESSION OWES A SECOND CARRIER FILE. Without this line a
   * handover whose recording never arrived looks identical to one that was never
   * recorded in the first place — and the second conversation is the half nobody
   * on our side heard, so its absence is the one most worth naming.
   */
  if (state === 'ENDED_TRANSFERRED') expected.push('telephony-transfer')
  /**
   * A TAKEN-OVER SESSION OWES THE TAKEOVER RECORDING, for the same reason: the
   * part after the takeover is a second conversation, recorded only by our DO
   * as it relayed it (docs/hitl.md §5), and nothing else holds it.
   */
  if (state === 'ENDED_TAKEN_OVER') expected.push('takeover')
  return expected.filter((t) => !recordings.some((r) => r.track === t))
}

export function SessionDetailPage({ id }: { id: string }) {
  const [data, setData] = useState<SessionDetail | null>(null)
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [epoch, setEpoch] = useState(0)
  const [statsOpen, setStatsOpen] = useState(false)
  const [logOpen, setLogOpen] = useState(false)
  /** Playback position translated onto the session's clock, for the transcript
   *  highlight. Null whenever nothing is playing. */
  const [playheadEpochMs, setPlayheadEpochMs] = useState<number | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    sessions
      .get(id, ac.signal)
      .then((d) => {
        setData(d)
        setError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
    return () => ac.abort()
  }, [id, epoch])

  // Same rule as the list: poll only while the conversation is still happening.
  // A finished session is immutable apart from finalisation, which the reader can
  // reload for.
  const anyLive = data ? isLive(data.session.state) : false
  useEffect(() => {
    if (!anyLive) return
    const t = setInterval(() => setEpoch((n) => n + 1), POLL_MS)
    return () => clearInterval(t)
  }, [anyLive])
  /** The live console saw the call end: reload now rather than at the next poll. */
  const reload = useCallback(() => setEpoch((n) => n + 1), [])

  if (error instanceof ApiError && error.notFound) {
    return (
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">No such session</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Nothing is recorded under <span className="mono">{id}</span>. Session ids are ours
          and are minted when a call is created; if this one came out of a chat message it
          may have been truncated. <Link to="/" className="underline underline-offset-4">
            Back to the session list
          </Link>.
        </p>
      </div>
    )
  }

  // A poll that fails while the page already shows the call KEEPS the page, and
  // says so above it. Swapping the page for an error box would also unmount the
  // live console, and drop a person who holds the call off it over one failed
  // refresh.
  if (error && !data) {
    return (
      <div className="min-w-0">
        <p className="err">{error.message}</p>
      </div>
    )
  }

  if (!data) return <p className="loading">Loading session…</p>

  const { session: s, turns, toolCalls, recordings, events } = data
  /**
   * The caller hung up while the agent was still speaking. Derived, not stored:
   * a DELIBERATE end (ENDED_NORMAL) plus the assistant track's token oracle
   * (speech GENERATED) exceeding its RTP span (speech DELIVERED) is exactly that
   * event — the same three-signal reasoning the jobs judge uses for its
   * clean-end exception, from columns this payload already carries. The verdict
   * on such a session reads COMPLETE, because the recording holds everything
   * that was delivered; this badge is what says WHY the agent's last sentence
   * ends early in it.
   */
  const interrupted = (() => {
    if (s.state !== 'ENDED_NORMAL') return false
    const a = recordings.find((r) => r.track === 'assistant')
    if (!a || a.expectedMsFromTokens === null || a.expectedMsFromRtp === null) return false
    return a.expectedMsFromTokens > a.expectedMsFromRtp * 1.02
  })()
  /** Read once and used twice: beside the cost figure and under the grid. */
  const cacheSplit = cachedInputSplit(s)
  /**
   * The anchor that maps a playback position onto the transcript's clock.
   *
   * NOT `startedAt`. Capture begins AFTER the SDP exchange, so the row's start
   * runs a second or two ahead of second zero of the audio — enough to light
   * transcript rows visibly late. The audio's own end is a better anchor: the
   * capture runs to the end of the call, so `endedAt − longest track's actualMs`
   * is when second zero of the file actually happened. Falls back to `startedAt`
   * for live or unfinalised sessions, where the lag is the best we can do.
   */
  /**
   * The session's recordings, split into the conversations they belong to. A
   * transferred call has two and they are SEQUENTIAL — see
   * `splitRecordingsByLeg`, which owns the rule.
   */
  const legs = splitRecordingsByLeg(recordings)

  const playbackAnchorMs = (() => {
    const started = s.startedAt ? Date.parse(s.startedAt) : Number.NaN
    const ended = s.endedAt ? Date.parse(s.endedAt) : Number.NaN
    if (!Number.isNaN(ended)) {
      let longest = 0
      /**
       * THE CONVERSATION LEG ONLY. A transferred session's second recording is a
       * different conversation on a later clock, and `ended` is when THIS one
       * finished — so measuring back from a transfer recording that outlasted the
       * AI would slide every transcript highlight by the difference.
       */
      for (const r of legs.conversation) {
        if (r.actualMs != null && r.actualMs > longest) longest = r.actualMs
      }
      if (longest > 0) return ended - longest
    }
    return Number.isNaN(started) ? null : started
  })()

  return (
    <div className="min-w-0">
      {/* ---------------------------------------------------------------- 1 */}
      <div className="mb-4 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {s.agentName ?? 'Session'}
          </h1>
          <StatePill state={s.state} />
          <EvidencePill evidence={s.evidence} />
          {interrupted && (
            <span
              className="pill pill-plain"
              title="The caller ended the call while the agent was still speaking — the agent had more to say than was delivered. Everything that WAS delivered is in the recording, which is why the evidence still reads complete."
            >
              interrupted
            </span>
          )}
          {s.label && <span className="pill pill-plain">{s.label}</span>}
          <Button
            variant="outline"
            size="sm"
            className="ml-auto cursor-pointer"
            onClick={() => setStatsOpen(true)}
          >
            <ActivityIcon aria-hidden />
            Tech stats
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={() => setLogOpen(true)}
          >
            <ScrollTextIcon aria-hidden />
            Event log
          </Button>
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground">
          {fmtWhen(s.startedAt)} · {fmtDuration(s.durationMs)} ·{' '}
          {s.transport === 'sip' ? 'phone' : 'browser'} ·{' '}
          {fmtInt(s.turnCount)} turns · {fmtInt(s.toolCallCount)} tool calls ·{' '}
          {fmtCost(s.costUsd)}
          {s.sweptAt !== null && (
            <>
              {' · '}
              <span
                className="pill pill-plain"
                title={sweptTitle(s.sweptAt, s.sweepState === 'pending')}
              >
                {s.sweepState === 'pending' ? 'sweeper: artefacts pending' : 'settled by sweeper'}
              </span>
            </>
          )}
          {s.stateDetail !== null && s.stateDetail !== '' && <> · {s.stateDetail}</>}
        </p>
        {error && <p className="err text-sm">This page could not be refreshed: {error.message}</p>}
      </div>

      {/*
        THE LIVE CONSOLE, while the call is happening — including after a
        takeover, which `isLive` counts as live. Keyed by the session so a
        different call never inherits this one's socket or microphone.
      */}
      {anyLive && <LiveMonitor key={s.id} sessionId={s.id} onEnded={reload} />}

      <section className="panel">
        <h2>What this call was</h2>
        <div className="body">
          <div className="grid2">
            <Field label="Session id" hint="Ours. The key for the DO, the S3 prefix and every Neon row.">
              <Mono value={s.id} copyable />
            </Field>
            <Field
              label="Provider call id"
              hint="OpenAI's. Read server-side off the Location header of the 201, never reported by the client. Nullable and not unique per session — a reconnect or a handoff mints a new one."
            >
              {s.providerCallId ? (
                <Mono value={s.providerCallId} copyable />
              ) : (
                <span className="empty text-sm">never learned</span>
              )}
            </Field>
            <Field label="Agent" hint="Snapshot of the name at call time; the agent may have been renamed since.">
              {s.agentName ? (
                <Link to={`/agents/${s.agentId}`} className="text-sm underline underline-offset-4">
                  {s.agentName}
                </Link>
              ) : (
                <Mono value={s.agentId} />
              )}
            </Field>
            <Field label="Transport">
              <TransportIcon transport={s.transport} />
            </Field>
            <Field label="Started">
              <span className="text-sm">{fmtWhen(s.startedAt)}</span>
            </Field>
            <Field label="Ended" hint="Absent while live, and absent forever on a session we never saw end.">
              <span className="text-sm">{fmtWhen(s.endedAt)}</span>
            </Field>
            <Field
              label="Duration"
              /* THREE DIFFERENT DURATIONS EXIST FOR ONE CALL and this field had no
                 hint at all, which is how a reader comes to compare it with the
                 media length below and conclude something is missing. Wall clock is
                 ended_at − started_at; the recording section quotes the length of
                 the AUDIO (about a second shorter, because capture starts after the
                 SDP exchange); and the token oracles count only BILLED speech,
                 which on a 25s call is about 6.5s. Never the same number. */
              hint="Wall clock: ended_at − started_at, both stamped by our Durable Object. NOT the length of the recording (that is in ‘what we actually hold’, and is shorter because capture starts after the SDP exchange) and NOT billed audio (silence is not billed, so the token figures are far smaller)."
            >
              <span className="text-sm">{fmtDuration(s.durationMs)}</span>
            </Field>
            <Field
              label="Cost"
              hint="Priced by the jobs worker from the provider's own token accounting in response.done.usage — not from our clock — plus transcription, which is metered per minute of audio and appears in no usage payload at all. Each cached figure is a subset of ITS OWN modality's input total: cached text discounts text input, cached audio discounts audio input, and the two are never added together."
            >
              <span className="text-sm">{fmtCost(s.costUsd)}</span>
              {/* The prompt cache is the largest single lever on this figure, so
                  the field says so where the figure is and the paragraph under
                  the grid shows the counts it is derived from. */}
              {cacheSplit.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {cacheSplit.map((c) => `${c.modality} ${fmtPct(c.ratio)} cached`).join(' · ')}
                </span>
              )}
            </Field>
            {s.transport === 'sip' && (
              <Field label="Caller">
                <span className="text-sm">
                  {s.fromNumber ?? DASH} → {s.toNumber ?? DASH}
                </span>
              </Field>
            )}
          </div>
          {s.providerCallIds.length > 1 && (
            <p className="mt-3 text-xs text-muted-foreground">
              This session had {s.providerCallIds.length} provider calls:{' '}
              {s.providerCallIds.map((c, i) => (
                <span key={c}>
                  {i > 0 && ', '}
                  <span className="mono">{c}</span>
                </span>
              ))}
              . Nothing here is keyed by them — the session id is ours and survives a
              reconnect.
            </p>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------------------- 2 */}
      {/*
        THE CALL ITSELF: the recording drawn as waveforms with one playhead, and
        the transcript as a thread underneath. Playback highlights the row being
        spoken. Everything diagnostic — the turn timeline, the per-track
        completeness claims, the tool-call table — lives in the tech stats
        drawer, opened from the header.
      */}
      <section className="panel">
        <h2>
          What was said
          <span className="note">recording and transcript · interleaved by our receive time</span>
        </h2>
        <div className="body">
          {/*
            ONE PLAYER PER CONVERSATION, and a transferred call has two.
            `telephony-transfer` is the caller talking to a PERSON, and it begins
            after this recording ended — a second lane on this timeline would put
            it at t=0 and claim the human was speaking while the AI still had the
            call. The first live transfer showed exactly that, with both
            conversations audible at once underneath it.
          */}
          <SessionAudioPlayer
            sessionId={s.id}
            recordings={legs.conversation}
            onPlayhead={(sec) =>
              setPlayheadEpochMs(
                sec == null || playbackAnchorMs == null ? null : playbackAnchorMs + sec * 1000,
              )
            }
          />

          {legs.transfer.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium">
                After the transfer
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  caller and a person &middot; its own recording, its own clock
                </span>
              </h3>
              <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
                A separate carrier recording, starting when the desk answered. There is{' '}
                <strong>no transcript, no turn timeline and no cost</strong> for it — our view
                into the conversation ended with the AI&rsquo;s leg, so this is audio and a
                duration and nothing else. It does not drive the transcript below, and the gap
                between the two recordings is the phone ringing.
              </p>
              <div className="mt-2">
                {/* NO `onPlayhead`: the transcript belongs to the conversation
                    above, and highlighting its lines from this timeline would
                    attribute the AI's words to a human's conversation. */}
                <SessionAudioPlayer sessionId={s.id} recordings={legs.transfer} />
              </div>
            </div>
          )}

          {legs.takeover.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium">
                After the takeover
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  caller and a person &middot; its own recording, its own clock
                </span>
              </h3>
              <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
                Recorded by our own server while it relayed the call, starting when a person took it
                over from the panel: the caller on the left, the person on the right. There is{' '}
                <strong>no transcript, no turn timeline and no cost</strong> for it — the AI was hung
                up at the takeover, so this is audio and a duration and nothing else. It does not
                drive the transcript below.
              </p>
              <div className="mt-2">
                {/* NO `onPlayhead`, for the transfer player's reason: the
                    transcript belongs to the AI's conversation above. */}
                <SessionAudioPlayer sessionId={s.id} recordings={legs.takeover} />
              </div>
            </div>
          )}

          <div className="mt-4">
            {s.model === 'gpt-live-1' ? <LiveConversationThread key={id} id={id} ended={!anyLive} tools={toolCalls} playheadEpochMs={playheadEpochMs}>
              <TranscriptThread turns={turns} toolCalls={toolCalls} events={events} endedAt={s.endedAt} playheadEpochMs={playheadEpochMs} />
            </LiveConversationThread> :
            <TranscriptThread
              turns={turns}
              toolCalls={toolCalls}
              events={events}
              endedAt={s.endedAt}
              playheadEpochMs={playheadEpochMs}
            />
            }
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- 3 */}
      <TechStatsDrawer
        open={statsOpen}
        onOpenChange={setStatsOpen}
        session={s}
        turns={turns}
        toolCalls={toolCalls}
        recordings={recordings}
      />

      {/* --------------------------------------------------------------- 4 */}
      {/*
        THE RAW RECORD, in its own drawer beside the tech stats. The page is a
        claim; this is the evidence the claim was derived from. It is also the
        only view that costs a network round trip, and it does not spend one
        until somebody opens the drawer: 548 events and 228 KB for one 84-second
        call. The page's sections come out of Postgres (~18 curated rows per
        call — the ones you filter ACROSS sessions); this is the unsampled
        firehose out of S3. Two stores, two jobs; see the note on
        `sessions.eventLog`.
      */}
      <Sheet open={logOpen} onOpenChange={setLogOpen}>
        <SheetContent side="right" className="w-full data-[side=right]:sm:max-w-4xl">
          <SheetHeader>
            <SheetTitle>Event log</SheetTitle>
            <SheetDescription>
              Every provider frame, unsampled, read from S3 on demand — including the
              system prompt and the tool schemas, verbatim. The one thing scrubbed is
              credentials: any custom tool or webhook header reads redacted.
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8">
            <EventLogPanel
              defaultOpen
              fetchPage={(after, kind, signal) => sessions.eventLog(id, after, kind, signal)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

// ===========================================================================
// The tech stats drawer
// ===========================================================================

/**
 * Every diagnostic section, in the order the old page ran them: who was slow,
 * what we actually hold, what we did. Nothing here is new — the components are
 * the same ones the page used to render inline, with all their caveats intact —
 * it is only the audience that changed: this is for the person diagnosing the
 * call, and it stays out of the way of the person reading it.
 */
function TechStatsDrawer({
  open,
  onOpenChange,
  session: s,
  turns,
  toolCalls,
  recordings,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  session: SessionDetail['session']
  turns: TurnRow[]
  toolCalls: ToolCallRow[]
  recordings: RecordingRow[]
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full data-[side=right]:sm:max-w-4xl">
        <SheetHeader>
          <SheetTitle>Tech stats</SheetTitle>
          <SheetDescription>
            The measurement detail behind this call: per-turn timings, what each recording
            actually holds, and every tool call with its arguments and result.
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8">
          <section className="panel">
            <h2>
              Under the hood
              <span className="note">plumbing counters, the build, and what the cost is made of</span>
            </h2>
            <div className="body">
              <div className="grid2">
                <Field
                  label="Sideband re-attaches"
                  hint="How many times our second WebSocket into the OpenAI session had to be re-established. Non-zero means transcript gaps are EXPECTED for this call, and that token-derived completeness is less trustworthy."
                >
                  <span
                    className={s.sidebandReattaches > 0 ? 'text-sm font-semibold text-ev-warn' : 'text-sm'}
                  >
                    {fmtInt(s.sidebandReattaches)}
                  </span>
                </Field>
                <Field
                  label="Worker version"
                  hint="Git SHA of the build that handled this call. Without it, a deploy's WebSocket-termination burst is indistinguishable from a provider incident."
                >
                  {s.workerVersion ? (
                    <Mono value={s.workerVersion} />
                  ) : (
                    <span className="empty text-sm">unstamped</span>
                  )}
                </Field>
                <Field
                  label="Clock offset"
                  hint="Estimated browser-to-server offset from the minimum-RTT sample of an NTP-style exchange over the control socket. Every client-side anchor on this page was translated with it."
                >
                  <span className="text-sm">{s.clockOffsetMs == null ? DASH : fmtMs(s.clockOffsetMs)}</span>
                </Field>
              </div>
              <WhatTheCostIsMadeOf split={cachedInputSplit(s)} />
            </div>
          </section>

          <section className="panel">
            <h2>
              Who was slow
              <span className="note">one row per turn · every interval names its observer</span>
            </h2>
            <div className="body">
              <TurnTimeline turns={turns} />
            </div>
          </section>

          <section className="panel">
            <h2>
              What we actually hold
              <span className="note">one player per track, each with its own claim</span>
            </h2>
            <div className="body">
              {recordings.length === 0 ? (
                <p className="empty text-sm">
                  No recording rows exist for this session. On the browser path that means the
                  recorder never opened its media socket; on the phone path it means Twilio's
                  recording callback has not arrived yet.
                </p>
              ) : (
                <div className="flex flex-col gap-4">
                  {/*
                    A MISSING TRACK HAS TO BE VISIBLE WHERE THE PLAYERS ARE. Five of the
                    seven real sessions have a caller track and no assistant track at
                    all, and this section rendered one player and said nothing — so a
                    reader with a full transcript and a COMPLETE pill reasonably
                    concludes the call is recorded. `worstEvidence` folds over the rows
                    that exist, so nothing else on the page can catch it either.
                  */}
                  {missingExpectedTracks(s.transport, recordings, s.state).map((track) => (
                    <div
                      key={`missing-${track}`}
                      className="rounded-md border border-ev-warn-border bg-ev-warn-bg px-3 py-2 text-sm text-ev-warn"
                    >
                      <strong className="font-semibold">
                        No {track} track exists for this session.
                      </strong>{' '}
                      Not a recording that failed — no row was ever written, so nothing measured
                      it and nothing here accounts for it. On the browser path that means the
                      capture for this side never ran.
                    </div>
                  ))}
                  {recordings.map((r) => (
                    <TrackPlayer key={r.track} sessionId={s.id} rec={r} />
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="panel">
            <h2>
              What we did
              <span className="note">
                executed in the Durable Object · never in the browser
              </span>
            </h2>
            <div className="body flush">
              <ToolCalls calls={toolCalls} />
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ===========================================================================
// What the cost is made of
// ===========================================================================

/** One modality's input tokens and how many of them the provider replayed from
 *  its prompt cache. `cached` is a SUBSET of `total`, never an addition. */
interface InputSplit {
  modality: 'text' | 'audio'
  total: number
  cached: number
  ratio: number
}

/**
 * The cached share of each input modality, for the modalities this call actually
 * had. Empty when the call recorded no input tokens at all — a session that never
 * got a `response.done` has nothing to explain and must not be given a "0% cached".
 *
 * TWO ENTRIES AND NOT ONE, BECAUSE THE TWO RATES DIFFER BY 8x. Cached text is a
 * discount on text input and cached audio a discount on audio input; a combined
 * "cached" percentage would be an average of two unrelated prices and could not be
 * reconciled against the figure above by anybody.
 *
 * The ratio is clamped at 1 so a provider that reports more cached tokens than
 * total ones renders as 100% rather than as 106%. It is the same clamp `cost.ts`
 * applies to the money, for the same reason, and it is the only figure here that is
 * not the provider's own number verbatim.
 */
function cachedInputSplit(s: SessionDetail['session']): InputSplit[] {
  const parts: InputSplit[] = [
    { modality: 'text', total: s.textInputTokens, cached: s.cachedTextInputTokens, ratio: 0 },
    { modality: 'audio', total: s.audioInputTokens, cached: s.cachedAudioInputTokens, ratio: 0 },
  ]
  return parts
    .filter((p) => p.total > 0)
    .map((p) => ({ ...p, ratio: Math.min(1, p.cached / p.total) }))
}

/**
 * WHY THE COST IS WHAT IT IS, IN THE PROVIDER'S OWN COUNTS.
 *
 * The cache is the difference between a call costing what it should and costing
 * several times that, and it was invisible on this page while being wrong: one
 * column held the cached AUDIO count under the name `cachedInputTokens`, the
 * cached TEXT count was discarded before it reached a database, and this page
 * displayed $0.187033 for a call that cost $0.153625 — 21.7% high, with nothing on
 * screen a reader could have used to notice.
 *
 * IT DELIBERATELY QUOTES NO RATES AND RE-PRICES NOTHING. The platform has exactly
 * one price table and it is in the jobs worker (`workers/jobs/src/cost.ts`); a
 * second one in the browser, drifting a point release behind, is how a customer
 * gets shown two different figures for one call. So this says what was counted and
 * what share of it was cached, and leaves the money to the figure above.
 */
function WhatTheCostIsMadeOf({ split }: { split: InputSplit[] }) {
  if (split.length === 0) return null
  /**
   * A ZERO HERE IS AMBIGUOUS AND THE PAGE HAS TO SAY SO. `sessions` stores these
   * as `integer NOT NULL DEFAULT 0`, and a per-response figure the provider did
   * not report is summed as 0 — so "no cache hits on this modality" and "the
   * provider told us nothing" arrive as the same number. Every call recorded
   * before these two columns existed reads 0 for text for exactly that reason.
   */
  const unreported = split.filter((c) => c.cached === 0)
  return (
    <p className="mt-3 max-w-3xl text-xs text-muted-foreground">
      Input the provider charged for on this call:{' '}
      {split.map((c, i) => (
        <span key={c.modality}>
          {i > 0 && '; '}
          {fmtInt(c.total)} {c.modality} tokens, of which{' '}
          <strong className="font-semibold text-foreground">
            {fmtInt(c.cached)} ({fmtPct(c.ratio)})
          </strong>{' '}
          were prompt-cache hits
        </span>
      ))}
      . A cached count is a SUBSET of the total beside it, not an addition to it, and
      it is discounted against that modality&rsquo;s own rate — the two are never
      pooled, because cached text and cached audio are discounts on uncached rates
      that differ by 8x. The figure above also carries transcription,
      which is metered per minute of audio rather than per token and so appears in
      none of these counts.
      {unreported.length > 0 && (
        <>
          {' '}
          The {unreported.map((c) => c.modality).join(' and ')} cache read
          {unreported.length === 1 ? 's' : ''} zero here, and that is two facts
          wearing one number: it means either that nothing was replayed or that the
          provider never told us. Calls recorded before this platform captured the
          text cache read zero for the same reason, and their real figures survive
          only in the raw event log.
        </>
      )}
    </p>
  )
}

// ===========================================================================
// Recordings
// ===========================================================================

const TRACK_TEXT: Record<string, { label: string; what: string }> = {
  user: {
    label: 'User',
    what:
      'The Opus packets the browser SENT — post echo-cancellation, post noise-suppression, '
      + 'post encode. Evidentially this is what the model actually heard, not what the '
      + 'microphone picked up.',
  },
  assistant: {
    label: 'Assistant',
    what:
      // THIS SAID THE OPPOSITE AND WAS WRONG. It described the encoded-transform
      // path, which the assistant track cannot use: Chromium accepts a
      // receiver-side transform and never feeds it (docs/next-tasks.md §4b), so this track
      // is captured with a Web Audio tap. It IS this listener's rendition —
      // jitter buffer and packet-loss concealment included — which is the precise
      // opposite of what this sentence used to claim, on the one screen where the
      // claim matters. §6 forbids exactly that.
      'A rendition of what THIS CONNECTION PLAYED, captured with a Web Audio tap — '
      + 'jitter buffer and packet-loss concealment included. It is not the bytes the '
      + 'provider put on the wire: Chromium accepts a receiver-side encoded transform '
      + 'and never delivers frames to it, so those bytes are unreachable from a page. '
      + 'The browser is still the only custodian of this track — OpenAI archives '
      + 'nothing and the sideband carries no audio — so this is the only recording of '
      + 'it that exists. On telephony the carrier produces the file and there is no '
      + 'such caveat.',
  },
  'telephony-dual': {
    label: 'Telephony, dual channel',
    what:
      'Twilio’s carrier-side recording: channel 1 the caller, channel 2 the model. '
      + 'Always-on, independent of any client, and the only track on this platform that '
      + 'no browser could have tampered with.',
  },
  takeover: {
    label: 'After the takeover',
    what:
      'The conversation after a person took this call over from the panel: the caller on '
      + 'the left channel, the person on the right, 16 kHz. Recorded by our Durable Object '
      + 'from the live audio it relayed between the two, so it is what each side sent, not '
      + 'a carrier’s recording. A frame lost on the way is silence in the file, and the '
      + 'completeness verdict counts it.',
  },
}

/** The sentence a reader needs BEFORE pressing play. Never optimistic. */
function completenessSentence(r: RecordingRow): string {
  const oracle = Math.max(r.expectedMsFromTokens ?? 0, r.expectedMsFromRtp ?? 0) || null
  if (r.evidence === 'ABSENT') {
    return 'Nothing was captured for this track at all. There is no file.'
  }
  if (r.evidence === 'COMPLETE') {
    // WHAT WAS ACTUALLY CHECKED, not a template.
    //
    // This used to assert "the two independent oracles agree within 2%" and then
    // print the two numbers underneath — 63.9s and 103.2s on a real call, a 61%
    // gap. Self-refuting on the page, and it was never what the verdict
    // established: `judgeCompleteness` tests the PRIMARY ruler for that track and
    // treats the other as a bound, precisely because the two measure different
    // quantities. Mirrors the rule in workers/jobs/src/completeness.ts — that file
    // is the source of truth if they ever diverge.
    //
    // Assistant: tokens are primary (how much speech the provider says it
    // generated) and the RTP timeline is a ceiling. The tap records CONTINUOUSLY,
    // silence included, so holding far more than the token figure is expected.
    // User: the RTP timeline is primary and the token count is a FLOOR — only
    // ~6.5s of a 25s call is billed, because silence is not billed.
    // Oracle 3 where it exists: on a tap-captured track the token count measures
    // SPEECH and the file length does not, so `voicedMs` is the only quantity
    // comparable to it. Mirrors judgeCompleteness.
    const assistant = r.track === 'assistant'
    const voiced = r.voicedMs
    const primary = assistant ? r.expectedMsFromTokens : r.expectedMsFromRtp
    const other = assistant ? r.expectedMsFromRtp : r.expectedMsFromTokens
    const primaryName = assistant ? 'the provider’s audio-token accounting' : 'the browser’s RTP timeline'
    // Capitalised because it starts a sentence in the template below.
    const otherName = assistant ? 'The browser’s RTP timeline' : 'The provider’s audio-token accounting'
    const whyOtherDiffers = assistant
      ? 'which is the length of the captured stream rather than of the speech in it, so it is '
        + 'expected to be larger and is used only as a ceiling'
      : 'which counts only billed speech and not silence, so it is a floor rather than a duration'
    const held = assistant && voiced !== null
      ? `We hold ${fmtSeconds(r.actualMs)} of audio, ${fmtSeconds(voiced)} of it non-silent. `
        + `The non-silent figure is what was weighed, because the ruler counts speech and a `
        + `continuously-recorded file's LENGTH is not a measure of the speech in it. `
      : `We hold ${fmtSeconds(r.actualMs)} of audio. `
    /**
     * "NOTHING MEASURABLE IS MISSING" MUST NOT BE PRINTED ABOVE A LIST OF MEASURED
     * MISSING CHUNKS, and it was — on two of the seven real sessions.
     *
     * Session 54a32a42 renders COMPLETE, then "Nothing measurable is missing from
     * this track", then "chunks 19–27 missing, 16.3s of audio — client_tail_missing"
     * on the next line. 16.3s of a 55s call, contradicted one line later, on the
     * panel whose entire job is honesty.
     *
     * Both facts are in the row, and neither is a lie — they were established by
     * DIFFERENT MEASUREMENTS AT DIFFERENT TIMES. `judgeCompleteness` is handed
     * `seqGaps` = the holes the jobs worker finds in the ASSEMBLED object's own seq
     * coverage (finalize.ts, `gapsFound`); the `gaps` COLUMN is `mergeGaps` of that
     * with everything the Durable Object recorded DURING the call. So a gap the DO
     * claimed and the assembly then disproved stays in the column and never reaches
     * the verdict. That is exactly what happened here: the DO compared the browser's
     * last seq against `stagedMaxSeq` rather than against what it had RECEIVED, so a
     * tail it was still holding was filed as missing and then staged by the deferred
     * hand-off. session-do.ts's own docblock documents this row by its numbers.
     *
     * Recomputed independently: 2,716 packets × 20 ms = 54,320 ms = `actual_ms` =
     * `expected_ms_from_rtp`, and `chunks_received` 28 = `last_seq` 40 + 1 for a
     * contiguous 0–27. Every chunk is present. The COMPLETE verdict is right and the
     * gap claim is stale.
     *
     * SO: never assert nothing is missing while the row lists something missing.
     * Say which measurement won and why, and leave the claim visible.
     */
    // Composed, not chosen: an assistant track with an unmeasured voiced duration
    // AND a retracted gap claim has to say both. Picking one dropped the other.
    const caveats: string[] = []
    if (r.gaps.length > 0) {
      caveats.push(
        `${r.gaps.length} gap claim${r.gaps.length === 1 ? '' : 's'} recorded DURING the call `
        + 'and listed below did not survive assembly: the verdict was reached from the '
        + 'finished object’s own chunk coverage, which is complete, so those claims are '
        + 'unconfirmed rather than confirmed missing. They are left on the page because a '
        + 'retracted gap and a hidden gap must not look the same.',
      )
    }
    if (assistant && voiced === null) {
      caveats.push(
        'Note this is a WEAKER claim than the caller’s track: non-silent duration was not '
        + 'measured for this recording, so a file of the right length that captured silence '
        + 'instead of speech would not be caught by this check.',
      )
    }
    if (caveats.length === 0) caveats.push('Nothing measurable is missing from this track.')
    return (
      held
      + `The ruler for this track is ${primaryName}, at ${fmtSeconds(primary)}, and what was `
      + `weighed is within tolerance of it. ${otherName} says ${fmtSeconds(other)} — `
      + `${whyOtherDiffers}. ${caveats.join(' ')}`
    )
  }
  if (r.evidence === 'PARTIAL') {
    const gapMs = r.gaps.reduce((sum, g) => sum + g.ms, 0)
    return (
      `We hold ${fmtSeconds(r.actualMs)} of an expected ${fmtSeconds(oracle)} — about `
      + `${fmtPct(r.completeness)} of this track. ${r.gaps.length} gap`
      + `${r.gaps.length === 1 ? '' : 's'} were measured, totalling ${fmtSeconds(gapMs)}. `
      + 'What is missing is missing; what is here is bit-exact.'
    )
  }
  if (r.evidence === 'UNVERIFIED') {
    /**
     * THIS ASSERTED A CAUSE, AND ON THE ONE REAL SESSION THAT SHOWS IT THE CAUSE IS
     * FALSE. Session 13fb7248's user track reads UNVERIFIED and the sentence said
     * "which is what happens when the Durable Object restarts". That call has
     * `sideband_reattaches = 0`, one `worker_version` throughout, and no restart
     * event anywhere in its 722-line event log. What actually happened is the OTHER
     * branch of `judgeCompleteness`: both oracles were present and one of them was
     * impossible — 215.1s of "billed user speech" claimed as a FLOOR under an 80.3s
     * recording, because that row's token oracle predates the cache-subtraction fix
     * in completeness.ts (`(2151 − 1344) × 100 ms` = 80.7s, 0.5% from what we hold).
     *
     * A restart is ONE possible cause and it is worth naming, but naming it as THE
     * cause turns "we could not measure" into a wrong diagnosis — the exact §6
     * failure, in the state that exists to admit ignorance. So: print both oracle
     * figures, say which shape of failure this row has, and offer the restart as a
     * possibility rather than a finding.
     */
    const tok = r.expectedMsFromTokens
    const rtp = r.expectedMsFromRtp
    const both = tok !== null && rtp !== null
    const why = both
      ? `Both oracles reported — the provider’s audio-token accounting says ${fmtSeconds(tok)} `
        + `and the browser’s RTP timeline says ${fmtSeconds(rtp)} — and they cannot both be `
        + 'right, so no completeness claim is made from either.'
      : tok === null && rtp === null
        ? 'Neither the provider’s token accounting nor the browser’s RTP timeline was '
          + 'available, so there was nothing to measure against.'
        : `Only ${tok === null ? 'the browser’s RTP timeline' : 'the provider’s token accounting'} `
          + 'was available, and on this track that is the loose bound rather than the tight '
          + 'ruler — a floor and a ceiling are not the same instrument.'
    /**
     * THE CARRIER TRACK NEEDS ITS OWN SENTENCE, because every clause of the browser
     * one is wrong about it.
     *
     * Rendered against the first real phone call (4c339d9a) this said: "Neither the
     * provider's token accounting nor the browser's RTP timeline was available, so
     * there was nothing to measure against… One cause is a Durable Object restart."
     * Three false things in two sentences:
     *
     *  · Those two oracles are NULL ON PURPOSE here, not missing. `writeTelephonyRow`
     *    spends a paragraph on why: they measure a capture pipeline of ours, and on
     *    this path there is none. Reporting a deliberate NULL as an absent
     *    measurement invites someone to go looking for the bug that isn't there.
     *  · "Nothing to measure against" was FALSE on that call. Twilio declared
     *    79,000 ms, `judgeCarrierRecording` weighed it, and the manifest beside the
     *    audio records COMPLETE at 0.9973. The ROW is what was stale.
     *  · A Durable Object restart cannot damage this file. No DO is in the media
     *    path — the carrier records the bridge on its own equipment. Naming it as a
     *    cause is a category error, and it points the reader at the wrong system.
     */
    if (r.track === 'telephony-dual') {
      return (
        `We hold ${fmtSeconds(r.actualMs)} of carrier-attested audio and are making no `
        + 'completeness claim about it. The two oracles this platform normally uses are '
        + 'deliberately absent here — both measure a capture pipeline of ours, and on this '
        + 'path there is none between the carrier and this file. The only ruler is the '
        + 'duration Twilio itself declared for the recording, and it was not recorded on '
        + 'this row. Check the manifest stored next to the audio: it carries the carrier’s '
        + 'declared duration and the verdict weighed against it, and it may well be a '
        + 'stronger claim than this row is making. Nothing here suggests the file is '
        + 'damaged — a Durable Object restart, which is the usual suspect on the browser '
        + 'tracks, cannot affect this one at all.'
      )
    }
    return (
      `We hold ${fmtSeconds(r.actualMs)} of audio and WE COULD NOT MEASURE how much of the `
      + `track that is. ${why} One cause is a Durable Object restart — the same event that `
      + 'most often damages the recording in the first place — so check the sideband '
      + 're-attach count above before assuming it. This file may be the entire track or may '
      + 'be missing minutes of it, and we cannot tell you which.'
    )
  }
  return 'Finalisation has not run for this track yet, so no claim is being made about it.'
}

function TrackPlayer({ sessionId, rec }: { sessionId: string; rec: RecordingRow }) {
  const [audio, setAudio] = useState<AudioUrl | null>(null)
  const [audioError, setAudioError] = useState<string | null>(null)
  const text = TRACK_TEXT[rec.track] ?? { label: rec.track, what: '' }

  useEffect(() => {
    // Only ask for a signed URL for something that exists. Signing is an HMAC
    // and an expiry, so it is cheap, but asking for a key that is null returns a
    // 404 that reads as a failure when it is really just "not finalised yet".
    if (rec.state !== 'READY' || rec.s3Key == null) return
    const ac = new AbortController()
    sessions
      .audioUrl(sessionId, rec.track, ac.signal)
      .then(setAudio)
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setAudioError(e instanceof Error ? e.message : String(e))
      })
    return () => ac.abort()
  }, [sessionId, rec.track, rec.state, rec.s3Key])

  const weak = rec.evidence !== 'COMPLETE' && rec.evidence !== 'PENDING'

  return (
    <div className="rounded-md border">
      <div className="flex flex-wrap items-center gap-2 border-b bg-muted/50 px-3 py-2">
        <span className="text-sm font-semibold">{text.label}</span>
        <EvidencePill evidence={rec.evidence} />
        <RecordingPill state={rec.state} />
        <span className="ml-auto text-xs text-muted-foreground">
          {rec.codec ?? DASH}
          {rec.sampleRate ? ` · ${(rec.sampleRate / 1000).toFixed(0)} kHz` : ''} ·{' '}
          {fmtBytes(rec.bytes)} · {fmtInt(rec.packetsReceived)} packets in{' '}
          {fmtInt(rec.chunksReceived)} chunks
        </span>
      </div>

      <div className="flex flex-col gap-2 p-3">
        {text.what && <p className="max-w-3xl text-xs text-muted-foreground">{text.what}</p>}
        {/*
          WHERE THAT SENTENCE CAME FROM, which is the difference between a stored
          fact and a guess that happens to be right today.
          `recordings.provenance` exists precisely so this claim is READ and not
          INFERRED — api.ts says so in as many words, and the schema calls it "the
          load-bearing field for every claim this product makes about an artefact".
          The paragraph above is still keyed on the TRACK NAME, which is the
          inference that field replaced. It is currently NULL on all nine real rows,
          so silently trusting it would be worse; saying which of the two the reader
          is looking at is the honest middle.
        */}
        <p className="max-w-3xl text-xs text-muted-foreground/80">
          {rec.provenance === null ? (
            <>
              Provenance for this row was <strong className="font-semibold">not recorded</strong>{' '}
              (<span className="mono">recordings.provenance</span> is null), so the sentence
              above is inferred from the track name and the transport rather than read from
              the artefact&rsquo;s own metadata. It is the best claim available, not a stored
              one.
            </>
          ) : (
            <>
              Provenance, as stored with the artefact:{' '}
              <span className="mono">{rec.provenance}</span>
              {rec.provenance === 'wire'
                ? ' — the bytes that crossed the network.'
                : rec.provenance === 'rendition'
                  ? ' — what this connection played, not what was sent.'
                  : rec.provenance === 'carrier'
                    ? ' — produced by the carrier, independent of any client.'
                    : rec.provenance === 'relay'
                      ? ' — recorded by our own server from the audio it relayed.'
                      : ' — a value this build does not recognise; treat it as unknown.'}
            </>
          )}
        </p>

        {/*
          THE CLAIM GOES ABOVE THE CONTROLS. A player is an implicit assertion
          that what you are about to hear is the thing you asked for, so anything
          qualifying that assertion has to arrive first — after the audio has
          played it is too late to matter.
        */}
        <p
          className={
            weak
              ? 'max-w-3xl rounded-md border border-ev-warn-border bg-ev-warn-bg px-3 py-2 text-sm text-ev-warn'
              : 'max-w-3xl text-sm text-muted-foreground'
          }
        >
          {weak && (
            <strong className="font-semibold">
              {rec.evidence === 'PARTIAL'
                ? `Partial recording — about ${fmtPct(rec.completeness)} of this track. `
                : rec.evidence === 'UNVERIFIED'
                  ? 'Completeness unknown. '
                  : 'Nothing captured. '}
            </strong>
          )}
          {completenessSentence(rec)}
        </p>

        {rec.gaps.length > 0 && (
          <>
            {/*
              THE LIST HAS TO SAY WHETHER THE VERDICT BELIEVED IT. `recordings.gaps`
              is the union of what the DO claimed during the call and what the jobs
              worker found in the assembled object; only the second reaches
              `judgeCompleteness`. So a COMPLETE row with entries here is not a
              contradiction, it is a retraction — and rendering the two identically
              is what made it read as one. See completenessSentence for the real
              session this happened on and how it was recomputed.
            */}
            <p className="text-xs font-semibold text-muted-foreground">
              {rec.evidence === 'COMPLETE'
                ? 'Gap claims recorded during the call — NOT confirmed by the finished object:'
                : 'Measured gaps:'}
            </p>
            <ul className="ml-4 list-disc text-xs text-muted-foreground">
              {rec.gaps.map((g) => (
                <li key={`${g.fromSeq}-${g.toSeq}-${g.reason}`}>
                  chunks <span className="mono">{g.fromSeq}</span>–
                  <span className="mono">{g.toSeq}</span>{' '}
                  {rec.evidence === 'COMPLETE' ? 'claimed missing' : 'missing'},{' '}
                  <strong className="font-semibold text-foreground">{fmtSeconds(g.ms)}</strong> of
                  audio — {g.reason}
                </li>
              ))}
            </ul>
          </>
        )}

        {audio ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio className="w-full" controls preload="metadata" src={audio.url} />
        ) : audioError ? (
          <p className="err text-xs">
            Could not issue a playback URL: {audioError}. The object may still exist — the
            key is <span className="mono">{rec.s3Key ?? 'unset'}</span>.
          </p>
        ) : rec.state === 'READY' ? (
          <p className="loading text-xs">Signing a playback URL…</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            No player yet: this track is <span className="mono">{rec.state}</span>. A URL is
            only issued once a final object exists.
          </p>
        )}
      </div>
    </div>
  )
}


// ===========================================================================
// Tool calls
// ===========================================================================

function ToolCalls({ calls }: { calls: SessionDetail['toolCalls'] }) {
  if (calls.length === 0) {
    return (
      <p className="loading">
        No tool was called. Tools are defined in the initial session config and executed in
        the Durable Object, so a browser could not have called one without us recording it
        here.
      </p>
    )
  }
  return (
    <div className="tablewrap">
      <table>
        <thead>
          <tr>
            <th>Tool</th>
            <th style={{ width: '4rem' }}>Turn</th>
            <th style={{ width: '7rem' }} className="text-right">
              Duration
            </th>
            <th style={{ width: '9rem' }}>Outcome</th>
            <th>Arguments and result</th>
          </tr>
        </thead>
        <tbody>
          {calls.map((c) => (
            <tr key={c.callId}>
              <td>
                <span className="mono">{c.name}</span>
              </td>
              <td className="num">{c.turnN ?? DASH}</td>
              <td className="num">{fmtMs(c.durationMs)}</td>
              <td className="text-xs">
                {/*
                  The evidence palette, NOT the `.ev-*` classes. Those carry a
                  specific meaning — what we can claim about a recording — and a
                  dashed UNVERIFIED pill on a tool that returned an error would
                  say something false about a different subject. Same colours,
                  different vocabulary.
                */}
                {c.ok === true && (
                  <span className="pill border-ev-good-border bg-ev-good-bg font-semibold text-ev-good">
                    ok
                  </span>
                )}
                {c.ok === false && (
                  <span className="pill border-ev-bad-border bg-ev-bad-bg font-semibold text-ev-bad">
                    error
                  </span>
                )}
                {c.ok == null && (
                  <span
                    className="pill pill-plain"
                    title="No output was recorded for this call. A tool that sends nothing leaves the model waiting forever, so this is a bug in the tool, not a state."
                  >
                    no output
                  </span>
                )}
                {c.orphaned && (
                  <span
                    className="pill pill-plain ml-1"
                    title="The response this call belonged to was cancelled — the user barged in while the tool was running. The side effect still happened; the model never saw the result."
                  >
                    orphaned
                  </span>
                )}
                {c.errorMessage && (
                  <div className="mt-1 text-ev-bad">{c.errorMessage}</div>
                )}
              </td>
              <td>
                {/*
                  BEHIND A DISCLOSURE, and a native <details> rather than a
                  dialog: arguments are MODEL OUTPUT and results are TOOL OUTPUT,
                  so they are debugging material and not evidence that anything
                  was authorised. Expanded by default they would make this table
                  unreadable; in a modal they would be two clicks from the row
                  they belong to.
                */}
                <details>
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                    show
                  </summary>
                  <div className="mt-1 flex flex-col gap-1">
                    <Json label="arguments" value={c.args} />
                    <Json label="result" value={c.result} />
                  </div>
                </details>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="p-3 text-xs text-muted-foreground">
        Arguments are model output; results are tool output. Neither is evidence that
        anything was authorised — authority lives in the tool implementation, never in a
        prompt or in an argument the model chose.
      </p>
    </div>
  )
}

function Json({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-wide text-muted-foreground/70">{label}</span>
      <pre className="mono mt-0.5 max-h-48 overflow-auto rounded border bg-muted/50 p-2 text-xs">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}
